import { RouteProp, useRoute } from '@react-navigation/native';
import React from 'react';
import { Platform, View } from 'react-native';
import styled from 'styled-components/native';
import CompareChart from '../../../../components/charts/CompareChart';
import DataDiary, {
  DiaryRecord
} from '../../../../components/charts/DataDiary';
import DataTrackerChart from '../../../../components/charts/DataTrackerChart';
import GaugeChart from '../../../../components/charts/GaugeChart';
import { B, H4, H5 } from '../../../../components/Typography';
import populationDataStudy from '../../study/pain/sampleData/population_data_study.json';
import mannWhitneyU from '../../../../common/mannwhitneyu';
import { ExamplesParamList } from '../../../../navigation/ExamplesNavigator';
import { gaugeChartValues } from '../sleep/SleepStudyResultsExampleScreen';
import { getImpactMeterProgressValue } from '../../../study/screen-components/plugins/ImpactGaugeCardPlugin';

export type DailyPainStudyData = {
  name?: string;
  day: number;
  intervention: string;
  dosage: number;
  painLevel: number;
  worstPain: number;
};

export type StudyImprovement = {
  numOfParticpants: number;
  value: number;
};

export type StudyResultGraphData = {
  compareGraphData: any;
  userLevelOfImprovement: number;
  xAxisMin: number;
  xAxisMax: number;
  yAxisMax: number;
};

const StyledScrollView = styled.ScrollView.attrs(() => ({
  scrollIndicatorInsets: { right: 1 },
  contentContainerStyle: {
    flexGrow: 1,
    justifyContent: 'space-between'
  }
}))`
  flex: 1;
`;

const StyledContainer = styled.View`
  padding: 20px;
  background-color: white;
  ${Platform.OS === 'web' ? 'padding-top: 80px;' : ''}
`;

const StyledGraphTitleView = styled.View`
  margin-top: 30px;
  margin-bottom: 20px;
`;

const StyledGaugeContainer = styled.View`
  padding: 40px 60px 20px 60px;
  margin: 0 20px 10px 20px;
`;

const PainStudyResultsExampleScreen: React.FC = () => {
  const route =
    useRoute<RouteProp<ExamplesParamList, 'PainStudyResultsExampleScreen'>>();
  const title = route.params?.title || '';
  const subTitle = route.params?.subTitle || '';
  const studyNumber = route.params?.studyNumber || 1;

  let studyUserData: Array<DailyPainStudyData> = require(`../../study/pain/sampleData/study_1_user_data.json`);
  const intervention1 = 'Washout';
  let intervention2 = 'Vitamin C';
  let populationDataStartingIndex = 100;

  if (studyNumber === 2) {
    studyUserData = require(`../../study/pain/sampleData/study_2_user_data.json`);
    intervention2 = 'ReLeaf';
    populationDataStartingIndex = 0;
  }

  const studies = ['painLevel', 'worstPain'];

  return (
    <StyledScrollView>
      <StyledContainer>
        <H4>{title}</H4>
        <H5>{subTitle}</H5>
        <StyledGraphTitleView>
          <B color='dark'>Data Diary</B>
        </StyledGraphTitleView>
        <DataDiary
          diaryData={generateDataDiaryData(studyUserData)}
          currentDay={studyUserData.length}
        />
        {studies.map(study => {
          const graphData = generateGraphData(
            intervention1,
            intervention2,
            studyUserData,
            study,
            populationDataStartingIndex
          );
          let studyName = 'Pain Level';
          const units = '';
          const dataTrackerYAxisMaxValue = 10;
          if (study === 'worstPain') {
            studyName = 'Worst Pain';
          }
          return (
            <View key={studyName}>
              <H5 style={{ marginTop: 30 }}>{studyName}</H5>
              <StyledGraphTitleView>
                <B color='dark'>Data Tracker</B>
              </StyledGraphTitleView>
              <DataTrackerChart
                yAxisLeftLabel={`${studyName}${units && ` (${units})`}`}
                yAxisRightLabel='DOSE (MG)'
                data={generateDataTrackerData(studyUserData, study)}
                xAxisMaxValueLabel={studyUserData.length}
                yAxisMaxValueLabel={dataTrackerYAxisMaxValue}
              />
              <StyledGraphTitleView>
                <B color='dark'>Compare</B>
              </StyledGraphTitleView>
              <CompareChart
                intervention={intervention2}
                subject='Pain'
                study={studyName}
                xAxisValueUnits={units}
                data={graphData.compareGraphData}
                position={graphData.userLevelOfImprovement}
                xAxisMinValueLabel={Math.min(
                  graphData.xAxisMin,
                  graphData.userLevelOfImprovement
                )}
                xAxisMaxValueLabel={Math.max(
                  graphData.xAxisMax,
                  graphData.userLevelOfImprovement
                )}
                yAxisMaxValueLabel={graphData.yAxisMax}
              />
              <StyledGraphTitleView>
                <B color='dark'>Impact</B>
              </StyledGraphTitleView>
              <StyledGaugeContainer>
                <GaugeChart
                  value={generateCorrelationValue(
                    studyUserData,
                    intervention1,
                    intervention2,
                    study
                  )}
                  values={gaugeChartValues}
                  label={studyName}
                />
              </StyledGaugeContainer>
            </View>
          );
        })}
      </StyledContainer>
    </StyledScrollView>
  );
};

function generateGraphData(
  intervention1: string,
  intervention2: string,
  studyUserData: Array<DailyPainStudyData>,
  study: string,
  populationDataStartingIndex = 0
): StudyResultGraphData {
  const intervention1Data = studyUserData.filter(
    item => item.intervention === intervention1
  );
  const intervention2Data = studyUserData.filter(
    item => item.intervention === intervention2
  );

  const painLevelImprovementSummary = generateSummaryOfImprovementLevel(
    intervention1,
    intervention2,
    populationDataStudy as any as Array<DailyPainStudyData>,
    populationDataStartingIndex,
    study
  );
  const compareGraphData = generateCompareGraphData(
    painLevelImprovementSummary
  );

  const result1 = getAverageImprovement(intervention1Data, study);
  const result2 = getAverageImprovement(intervention2Data, study);
  const userLevelOfImprovement = +(result2 - result1).toFixed(1);

  const xAxisMin = Math.min(...compareGraphData.map((data: any) => data.x));
  const xAxisMax = Math.max(...compareGraphData.map((data: any) => data.x));
  const yAxisMax =
    Math.max(...compareGraphData.map((data: any) => data.y)) * 1.3;

  const a: StudyResultGraphData = {
    userLevelOfImprovement,
    compareGraphData,
    xAxisMin,
    xAxisMax,
    yAxisMax
  };

  return a;
}

function generateDataTrackerData(studyData: any, study: string): any {
  return [
    studyData.map((data: any) => {
      return { x: data.day, y: data[`${study}`] };
    }),
    studyData.map((data: any, index: number) => {
      return {
        x: data.day,
        y: data.dosage,
        label: index === studyData.length - 1 ? 'Dose (mg)' : null
      };
    })
  ];
}

function generateDataDiaryData(studyData: any): DiaryRecord {
  return {
    days: studyData.length,
    maxDose: Math.max(...studyData.map((data: any) => data.dosage)) * 2,
    records: studyData.map((data: any) => {
      return {
        day: data.day,
        events: [
          {
            name: 'Pain Scale',
            done: true
          },
          {
            name: 'Daily Dose',
            done: true
          }
        ]
      };
    }),
    doseSched: studyData.map((data: any) => {
      return {
        day: data.day,
        doseMg: data.dosage
      };
    })
  };
}

function getAverageImprovement(data: Array<any>, study: string): number {
  const sum = data.reduce((a, v) => a + v[`${study}`], 0);
  return sum / data.length;
}

function generateSummaryOfImprovementLevel(
  intervention1: string,
  intervention2: string,
  data: Array<DailyPainStudyData>,
  startingIndex = 0,
  study: string
): Array<StudyImprovement> {
  const summaryOfImprovementLevel: Array<StudyImprovement> = [];
  for (let index = 1; index <= 100; index++) {
    const intervention1Data = data.filter(
      item =>
        item.name === `Pain Person ${index + startingIndex}` &&
        item.intervention === intervention1
    );
    const intervention2Data = data.filter(
      item =>
        item.name === `Pain Person ${index + startingIndex}` &&
        item.intervention === intervention2
    );
    const result1 = getAverageImprovement(intervention1Data, study);
    const result2 = getAverageImprovement(intervention2Data, study);

    const improvementLevel = +(result2 - result1).toFixed(1);

    const index = summaryOfImprovementLevel.findIndex(
      item => item.value === improvementLevel
    );
    if (index > -1) {
      summaryOfImprovementLevel[index].numOfParticpants++;
    } else {
      summaryOfImprovementLevel.push({
        numOfParticpants: 1,
        value: improvementLevel
      });
    }
  }
  return summaryOfImprovementLevel;
}

function generateCompareGraphData(summary: Array<StudyImprovement>): any {
  return summary.map(data => {
    return {
      x: data.value,
      y: (data.numOfParticpants / 100) * 100
    };
  });
}

export function generateCorrelationValue(
  studyUserData: Array<DailyPainStudyData>,
  intervention1: string,
  intervention2: string,
  study: string
): number {
  const intervention1Data = studyUserData
    .filter(item => item.intervention === intervention1)
    .map(item => item[`${study}`]);
  const intervention2Data = studyUserData
    .filter(item => item.intervention === intervention2)
    .map(item => item[`${study}`]);
  const pValue = mannWhitneyU.test(intervention1Data, intervention2Data).p;
  return getImpactMeterProgressValue(pValue);
}

export default PainStudyResultsExampleScreen;
